import { useState, useEffect, useCallback } from 'react';
import type { ListName, SPListItem } from '@/models/types';
import { useAppContext } from '@/store/appContext';
import { getListItems } from '@/graph/sharePointService';
import {
  createWithAudit,
  updateWithAudit,
  deleteWithAudit,
} from '@/graph/auditMiddleware';

/** Best-effort human-readable entity name from a partial fields object. */
function deriveEntityName(
  fields: Record<string, unknown> | unknown,
  fallback: string,
): string {
  const f = (fields ?? {}) as Record<string, unknown>;
  const candidates = [
    'Title',
    'Name',
    'DisplayName',
    'AppName',
    'EmployeeEmail',
    'EmployeeId',
    'Description',
  ];
  for (const k of candidates) {
    const v = f[k];
    if (typeof v === 'string' && v.length > 0) return v;
    if (typeof v === 'number') return String(v);
  }
  return fallback;
}

interface UseSharePointListOptions {
  filter?: string;
  top?: number;
  orderBy?: string;
  autoFetch?: boolean;
}

export function useSharePointList<T>(
  listName: ListName,
  options: UseSharePointListOptions = {},
) {
  const { graphClient, listIds } = useAppContext();
  const [items, setItems] = useState<SPListItem<T>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { autoFetch = true, filter, top, orderBy } = options;

  const refresh = useCallback(async () => {
    if (!graphClient || !listIds) return;

    setLoading(true);
    setError(null);

    try {
      const data = await getListItems<T>(graphClient, listName, {
        filter,
        top,
        orderBy,
      });
      setItems(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [graphClient, listIds, listName, filter, top, orderBy]);

  useEffect(() => {
    if (autoFetch && graphClient && listIds) {
      refresh();
    }
  }, [autoFetch, graphClient, listIds, refresh]);

  const create = useCallback(
    async (fields: Partial<T>): Promise<SPListItem<T> | null> => {
      if (!graphClient) return null;

      try {
        const entityName = deriveEntityName(fields, `${listName}#new`);
        const newItem = await createWithAudit<T>(
          graphClient,
          listName,
          fields,
          entityName,
        );
        setItems((prev) => [...prev, newItem]);
        return newItem;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to create';
        setError(message);
        return null;
      }
    },
    [graphClient, listName],
  );

  const update = useCallback(
    async (itemId: string, fields: Partial<T>): Promise<boolean> => {
      if (!graphClient) return false;

      try {
        const existing = items.find((i) => i.id === itemId);
        const entityName = existing
          ? deriveEntityName(existing.fields, `${listName}#${itemId}`)
          : deriveEntityName(fields, `${listName}#${itemId}`);
        await updateWithAudit<T>(
          graphClient,
          listName,
          itemId,
          fields,
          entityName,
        );
        setItems((prev) =>
          prev.map((item) =>
            item.id === itemId
              ? { ...item, fields: { ...item.fields, ...fields } }
              : item,
          ),
        );
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to update';
        setError(message);
        return false;
      }
    },
    [graphClient, listName, items],
  );

  const remove = useCallback(
    async (itemId: string): Promise<boolean> => {
      if (!graphClient) return false;

      try {
        const existing = items.find((i) => i.id === itemId);
        const entityName = existing
          ? deriveEntityName(existing.fields, `${listName}#${itemId}`)
          : `${listName}#${itemId}`;
        await deleteWithAudit(graphClient, listName, itemId, entityName);
        setItems((prev) => prev.filter((item) => item.id !== itemId));
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to delete';
        setError(message);
        return false;
      }
    },
    [graphClient, listName, items],
  );

  return {
    items,
    loading: loading && autoFetch,
    error,
    create,
    update,
    remove,
    refresh,
  };
}
